import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { createCustomPropType, isNumeric } from '../../js/app/react/validators';
import { Button } from '../CommonComponents/OPL/Button/Button';
import Tooltip from '../CommonComponents/Tooltip';
import FinancialPlanDropdown from '../FinancialPlanGoalDropdown/FinancialPlanGoalDropdownContainer';
import { PlansSelector } from '../FinancialPlanOPL/Goals/goals/Retirement/pages/RetirementSideBySide/components/PlansSelector/PlansSelector';
import { navigateToRetirementSideBySide } from '../FinancialPlanOPL/Goals/goals/Retirement/pages/RetirementSideBySide/utils/navigateToRetirementSideBySide';
import { showAddGoalSelector } from './GoalsSummary/GoalsSummarySelector';
import { PDFTitle } from '../PDFRenderer/components/PDFTitle/PDFTitle';
import { getGoalPageName } from '../FinancialPlanOPL/Goals/utils/getGoalPageName';

const onGoalChanged = (goalId, clientId, goalType) => {
  const { ADV } = window;

  if (goalId === 'summary') {
    ADV.util.Router.go2('GoalsSummary', {
      clientId,
    });
  } else {
    ADV.util.Router.go2(getGoalPageName(goalType), {
      clientId,
      goalId,
      workflowEnded: false,
    });
  }
};

const FinancialPlanHeaderView = ({
  isPrint,
  printTitle,
  selectedGoal,
  clientId,
  showCalibration,
  onOpenCalibration,
  hasTrackIndicator,
  showAddGoal,
}) => {
  const isWhatIfButtonMounted = useRef(false);
  const whatIfButton = useRef();

  useEffect(() => {
    if (showCalibration && isWhatIfButtonMounted.current) {
      whatIfButton.current.focus();
    } else if (showCalibration) {
      isWhatIfButtonMounted.current = true;
    } else if (!showCalibration && document.getElementById('what-ifs-container')) {
      document.getElementById('what-ifs-container').focus();
    }
  }, [showCalibration]);

  return (
    <div>
      <PDFTitle title={printTitle} />

      {!isPrint && <FinancialPlanDropdown clientId={clientId} onGoalChanged={onGoalChanged} />}

      {hasTrackIndicator && (
        <div className="track-indicator">
          <Tooltip
            tag="div"
            className="tooltip-action-needed"
            tooltipchildren={
              window.ADV.tooltip_callouts.FinancialPlan_Recommandations.ActionNeededLabel.html
            }
            orientation={
              window.ADV.tooltip_callouts.FinancialPlan_Recommandations.ActionNeededLabel
                .orientation
            }
          />
        </div>
      )}

      <div className="right-buttons">
        <div className="open-calibration">
          {!isPrint && selectedGoal?.GoalType === 'retirement' && (
            <PlansSelector
              clientId={clientId}
              anchorEl={
                <Button variant="secondary" leftIcon="far fa-clipboard-list-check">
                  Compare Plans
                </Button>
              }
              onApply={planIds => {
                navigateToRetirementSideBySide(clientId, planIds, selectedGoal.GoalId);
              }}
            />
          )}

          {!isPrint && showAddGoal && (
            <Button
              variant="secondary"
              leftIcon="far fa-flag"
              onClick={() => ADV.util.Router.go2('ClientEditPlan', { clientId })}
            >
              Add Goal
            </Button>
          )}

          {showCalibration && (
            <Button
              leftIcon="far fa-sliders-h"
              onClick={onOpenCalibration(clientId, selectedGoal.GoalType)}
              ref={whatIfButton}
            >
              What-Ifs
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

FinancialPlanHeaderView.defaultProps = {
  isPrint: false,
  printTitle: null,
  selectedGoal: undefined,
  onOpenCalibration: () => {},
  hasTrackIndicator: false,
};

FinancialPlanHeaderView.propTypes = {
  isPrint: PropTypes.bool,
  printTitle: PropTypes.string,
  selectedGoal: PropTypes.shape({
    GoalId: PropTypes.string,
    GoalType: PropTypes.string,
  }),
  clientId: createCustomPropType(isNumeric).isRequired,
  showCalibration: PropTypes.bool.isRequired,
  onOpenCalibration: PropTypes.func,
  hasTrackIndicator: PropTypes.bool,
  showAddGoal: PropTypes.bool.isRequired,
};

const mapStateToProps = state => ({
  showAddGoal: showAddGoalSelector(state),
});

export default connect(mapStateToProps)(FinancialPlanHeaderView);
